import Grid2 from '@mui/material/Unstable_Grid2';

import BlogArticle3Up from '@/components/BlogComponents/BlogArticle3Up/BlogArticle3Up';
import BlogHero from '@/components/BlogComponents/BlogHero/BlogHero';
import BlogInfo from '@/components/BlogComponents/BlogInfo/BlogInfo';
import BlogRichText from '@/components/BlogComponents/BlogRichText/BlogRichText';
import GridContainer2 from '@/components/Layout/GridContainer2/GridContainer2';
import DynamicSchema from '@/components/Schema/DynamicSchema';
import { Section } from '@/components/Section/Section';
import SkipToContent from '@/components/SkipToContent/SkipToContent';
import type { BlogPageContent, Seo } from '@/contentful';
import type { userLocationInterface } from '@/hooks/geo';

import useStyles from './BlogPage.styles';
import { FooterComponent, HeaderComponent } from './StandardPage';

interface Props extends userLocationInterface {
  path: string;
  seoFields: Seo;
  content: BlogPageContent;
}

export default function Blog(content: Props) {
  const { classes } = useStyles();
  const { userLocation, setUserLocation, seoFields, path } = content;
  const { fields, sys } = content.content;
  const {
    header,
    footer,
    sections,
    blogTitle,
    blogSubtitle,
    heroImage,
    heroImageAltText,
    video,
    author,
    publicationDate,
    lastUpdated,
    body,
    socialShare,
    showTableOfContents,
    backButton,
    curatedArticlesListHeading,
    linkedArticleContent,
  } = fields;

  // Determine which date to show and label
  const displayDate = lastUpdated ? lastUpdated : publicationDate;
  const dateLabel = lastUpdated ? 'Last Updated' : 'Published';

  return (
    <>
      <DynamicSchema
        path={path}
        headline={blogTitle}
        description={seoFields.fields.metaText || ''}
        image={heroImage?.[0]}
        datePublished={publicationDate || new Date(sys.createdAt)}
        dateModified={new Date(sys.updatedAt)}
        lastUpdated={lastUpdated}
      />
      <SkipToContent href='#blog' />
      <HeaderComponent
        header={header}
        userLocation={userLocation}
        setUserLocation={setUserLocation}
      />
      {/** biome-ignore lint/correctness/useUniqueElementIds: Automated migration to Biome */}
      <div className={classes.blog} id='blog'>
        <GridContainer2 fixed>
          <Grid2 className={classes.gridItem}>
            <BlogHero
              title={blogTitle}
              subtitle={blogSubtitle}
              image={heroImage}
              imageAltText={heroImageAltText}
              backButton={backButton}
              video={video}
            />
            <BlogInfo
              title={blogTitle}
              author={author}
              publicationDate={displayDate}
              dateLabel={dateLabel}
              socialShare={socialShare}
              blogBody={body}
              showTableOfContents={showTableOfContents}
            />
          </Grid2>
        </GridContainer2>
        <div className={classes.blogRichTextWrapper}>
          <BlogRichText text={body} />
        </div>

        {/** biome-ignore lint/complexity/useOptionalChain: Automated migration to Biome */}
        {linkedArticleContent &&
          linkedArticleContent.curatedArticlesListCollection && (
            <BlogArticle3Up
              isCurated
              heading={curatedArticlesListHeading}
              initialArticles={
                linkedArticleContent.curatedArticlesListCollection?.items
              }
            />
          )}

        {sections?.map((section, index) => (
          <Section
            key={section.sys.id}
            section={section}
            userLocation={userLocation}
            sectionPosition={index}
          />
        ))}
      </div>
      <FooterComponent
        footer={footer}
        userLocation={userLocation}
        setUserLocation={setUserLocation}
      />
    </>
  );
}
